import { useState, useRef, useEffect } from 'react';
import { Bell, User, Settings, CreditCard, HelpCircle, LogOut, Wifi, WifiOff, Menu, Moon, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useTheme } from '../hooks/useTheme';
import NextVideoCountdown from './NextVideoCountdown';

export default function Header({ isMobile, onMenuClick }) {
  const { user, studio, logout } = useAuth();
  const { isConnected } = useSocket();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  // Notifications - empty for now until notification system is built
  const notifications = [];

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ensure we're showing business user data, not customer data
  const displayName = (user?.role === 'student' || !user?.firstName) ? 'Demo' : (user?.firstName || 'Demo');

  return (
    <header style={{ backgroundColor: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)' }}>
      <div className="px-4 md:px-6 pt-4 md:pt-6 pb-3 md:pb-4">
        {isMobile ? (
          // Mobile Header
          <div className="flex items-center justify-between">
            {/* Hamburger Menu */}
            <button
              onClick={onMenuClick}
              className="p-2 rounded-lg transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Center - Studio Name / Welcome */}
            <div className="text-center flex-1 mx-2">
              <h2 className="text-base font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                Welcome, {displayName}!
              </h2>
            </div>

            {/* Right side - Connection + Theme + Notifications */}
            <div className="flex items-center gap-2">
              {/* Connection Status - Icon only on mobile */}
              <div
                className="p-1.5 rounded-lg"
                style={{
                  background: isConnected ? 'var(--secondary-12)' : 'rgba(239, 68, 68, 0.12)',
                }}
              >
                {isConnected ? (
                  <Wifi className="w-4 h-4" style={{ color: 'var(--secondary)' }} />
                ) : (
                  <WifiOff className="w-4 h-4" style={{ color: '#ef4444' }} />
                )}
              </div>

              {/* Theme Toggle - Mobile */}
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-lg transition-colors"
                style={{
                  backgroundColor: isDark ? 'var(--highlight-10)' : 'var(--highlight-5)',
                  color: 'var(--text-primary)'
                }}
              >
                {isDark ? (
                  <Sun className="w-4 h-4" />
                ) : (
                  <Moon className="w-4 h-4" />
                )}
              </button>

              {/* FEATURE_DISABLED: Notification bell - re-enable when ready */}
              {/* <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <Bell className="w-5 h-5" />
                  {notifications.length > 0 && (
                    <span
                      className="absolute top-1 right-1 w-2 h-2 rounded-full"
                      style={{ background: 'var(--primary)' }}
                    ></span>
                  )}
                </button>

                {showNotifications && (
                  <div
                    className="fixed inset-x-4 top-16 rounded-xl py-2 z-50 md:absolute md:inset-x-auto md:right-0 md:top-auto md:mt-2 md:w-80"
                    style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    }}
                  >
                    <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Notifications</h3>
                    </div>
                    <div className="max-h-64 md:max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className="px-4 py-3 cursor-pointer transition-colors"
                            style={{
                              background: notif.unread ? 'var(--highlight-8)' : 'transparent',
                            }}
                          >
                            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{notif.text}</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{notif.time}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div> */}

              {/* Profile - Icon only on mobile */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="p-2 rounded-lg transition-colors"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--primary-12)' }}
                  >
                    <User className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  </div>
                </button>

                {/* Mobile Profile Dropdown */}
                {showProfileMenu && (
                  <div
                    className="fixed inset-x-4 top-16 rounded-xl py-2 z-50 md:absolute md:inset-x-auto md:right-0 md:top-auto md:mt-2 md:w-56"
                    style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    }}
                  >
                    <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{user?.name}</p>
                      <p className="text-sm truncate" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                    </div>
                    {/* FEATURE_DISABLED: Subscription button in dropdown - re-enable when ready */}
                    {/* <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/subscription');
                      }}
                      className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <CreditCard className="w-5 h-5" />
                      <span>Subscription</span>
                    </button> */}
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Settings className="w-5 h-5" />
                      <span>Settings</span>
                    </button>
                    {/* FEATURE_DISABLED: Help & Support - re-enable when ready */}
                    {/* <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/help');
                      }}
                      className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <HelpCircle className="w-5 h-5" />
                      <span>Help & Support</span>
                    </button> */}
                    <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                      <button
                        onClick={logout}
                        className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors"
                        style={{ color: '#ef4444' }}
                      >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Desktop Header
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>Welcome back, {displayName}!</h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                {studio?.subscription_tier?.toUpperCase()} Plan • {studio?.subscriptionStatus || 'Active'}
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Connection Status */}
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{
                  background: isConnected ? 'var(--secondary-12)' : 'rgba(239, 68, 68, 0.12)',
                }}
              >
                {isConnected ? (
                  <Wifi className="w-4 h-4" style={{ color: 'var(--secondary)' }} />
                ) : (
                  <WifiOff className="w-4 h-4" style={{ color: '#ef4444' }} />
                )}
                <span
                  className="text-xs font-semibold"
                  style={{ color: isConnected ? 'var(--secondary)' : '#ef4444' }}
                >
                  {isConnected ? 'Connected' : 'Offline'}
                </span>
              </div>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: isDark ? 'var(--highlight-10)' : 'var(--highlight-5)',
                  color: 'var(--text-primary)'
                }}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {/* FEATURE_DISABLED: Notification bell (desktop) - re-enable when ready */}
              {/* <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <Bell className="w-6 h-6" />
                  {notifications.length > 0 && (
                    <span
                      className="absolute top-1 right-1 w-2 h-2 rounded-full"
                      style={{ background: 'var(--primary)' }}
                    ></span>
                  )}
                </button>

                {showNotifications && (
                  <div
                    className="absolute right-0 mt-2 w-80 rounded-xl py-2 z-50"
                    style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    }}
                  >
                    <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Notifications</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className="px-4 py-3 cursor-pointer transition-colors"
                            style={{
                              background: notif.unread ? 'var(--highlight-8)' : 'transparent',
                            }}
                          >
                            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{notif.text}</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{notif.time}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div> */}

              {/* Profile */}
              <div className="relative" ref={profileRef}>
                <div
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors"
                  style={{ background: 'var(--highlight-5)' }}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--primary-12)' }}
                  >
                    <User className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{studio?.name || 'Studio'}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Studio Owner</p>
                  </div>
                </div>

                {/* Profile Dropdown */}
                {showProfileMenu && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-xl py-2 z-50"
                    style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    }}
                  >
                    <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{user?.name}</p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                    </div>
                    {/* FEATURE_DISABLED: Subscription button in dropdown (desktop) - re-enable when ready */}
                    {/* <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/subscription');
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <CreditCard className="w-4 h-4" />
                      <span className="text-sm">Subscription</span>
                    </button> */}
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">Settings</span>
                    </button>
                    {/* FEATURE_DISABLED: Help & Support (desktop) - re-enable when ready */}
                    {/* <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/help');
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span className="text-sm">Help & Support</span>
                    </button> */}
                    <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                      <button
                        onClick={logout}
                        className="w-full px-4 py-2 text-left flex items-center gap-3 transition-colors"
                        style={{ color: '#ef4444' }}
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <NextVideoCountdown />
    </header>
  );
}
